import { Box, Collapse, Flex, Icon, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronRight, FaExclamationCircle } from "react-icons/fa";
import { EspdCriterionDto, EspdRequirementGroupDto, EspdRequirementResponseDto } from "../../../../autogen/bff-api";
import { EspdRepeatableGroupForm } from "./EspdRepeatableGroupForm";
import { EspdRequirementGroupForm } from "./EspdRequirementGroupForm";

interface Props {
  criterion: EspdCriterionDto;
  responses: Map<string, EspdRequirementResponseDto>;
  instanceCounts: Map<string, number>;
  onResponseChange: (requirementId: string, value: string | boolean | number | null, instanceIndex?: number | null) => void;
  onMetadataChange?: (requirementId: string, field: string, value: string | null, instanceIndex?: number | null) => void;
  onAddInstance: (group: EspdRequirementGroupDto) => void;
  onRemoveInstance: (group: EspdRequirementGroupDto, index: number) => void;
  readOnly?: boolean;
  hasErrors?: boolean;
  isValidating?: boolean;
}

export const EspdCriterionForm = ({ criterion, responses, instanceCounts, onResponseChange, onMetadataChange, onAddInstance, onRemoveInstance, readOnly, hasErrors, isValidating }: Props) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isNorwegian = i18n.language === "no" || i18n.language === "nb";
  const displayName = isNorwegian && criterion.nameNorwegian ? criterion.nameNorwegian : criterion.name;
  const displayDescription =
    isNorwegian && criterion.descriptionNorwegian ? criterion.descriptionNorwegian : criterion.description;

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p="4" mb="3" bg="gray.50">
      <Flex
        alignItems="center"
        gap="2"
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        mb={expanded ? "3" : "0"}
        py="1"
        borderRadius="md"
        _hover={{ bg: "gray.100" }}
      >
        <Icon as={expanded ? FaChevronDown : FaChevronRight} w="12px" h="12px" color="smMuted" />
        {hasErrors && <Icon as={FaExclamationCircle} color="orange.500" w="14px" h="14px" />}
        <Text fontWeight="semibold" fontSize="md">
          {displayName}
        </Text>
      </Flex>

      <Collapse in={expanded}>
        <Text fontSize="sm" color="smMuted" mb="3">
          {displayDescription}
        </Text>

        {criterion.requirementGroups.map((group) =>
          group.repeatable ? (
            <EspdRepeatableGroupForm
              key={group.id}
              group={group}
              responses={responses}
              instanceCount={instanceCounts.get(group.id) ?? 1}
              onResponseChange={onResponseChange}
              onMetadataChange={onMetadataChange}
              onAddInstance={onAddInstance}
              onRemoveInstance={onRemoveInstance}
              readOnly={readOnly}
              isValidating={isValidating}
            />
          ) : (
            <EspdRequirementGroupForm
              key={group.id}
              group={group}
              responses={responses}
              onResponseChange={onResponseChange}
              onMetadataChange={onMetadataChange}
              readOnly={readOnly}
              isValidating={isValidating}
            />
          )
        )}
      </Collapse>
    </Box>
  );
};
